import { LitElement } from 'lit';
import { PickAnAttribute } from '../../modules/PickAnAttribute.js';
import { getFirebaseApp } from '../../modules/katapult-job/KatapultJobUtilities.js';
import { BreakAngleAttributeService } from './BreakAngleAttributeService.js';

/**
 * Drives the Break Angle map tool: bulk-resolves the `break_angle` attribute for a set of nodes via
 * BreakAngleAttributeService, then walks the user through any nodes that couldn't be resolved automatically, one at
 * a time, so they can be reviewed manually.
 *
 * This is a headless coordinator - it owns the feature's selection/review state and calls the Service, but has no
 * visual template of its own. Every action that needs the host page's map, node info panel, or action dialog is
 * requested via a CustomEvent; the host (katapult-maps-desktop.js) is responsible for actually carrying it out.
 */
export class BreakAngleReviewTool extends LitElement {
  static properties = {
    /** The id of the job being edited. */
    jobId: { type: String },
    /** The host's client-side node cache (id -> node), used to read coordinates and existing attributes. */
    nodes: { type: Object },
    /** The attribute key that stores a connection's type (e.g. `connection_type`). */
    connectionTypeAttribute: { type: String },
    /** The attribute key used to label a node in error messages (e.g. `PL_number`). */
    orderingAttribute: { type: String },
    _reviewQueue: { state: true },
    _completedReviews: { state: true }
  };

  constructor() {
    super();
    this._reviewQueue = null;
    this._completedReviews = [];
    this._pausedForLineAngleCalculator = false;
  }

  /**
   * Dispatches a request event for the host to handle. Always bubbles/composed - this element normally sits inside
   * the host's own shadow DOM, and a plain (non-bubbling) CustomEvent would never reach a listener attached to the
   * host itself.
   * @param {string} name
   * @param {object} [detail]
   */
  _emit(name, detail) {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }

  /** Starts the node-selection step (polygon/select-all) for setting the Break Angle attribute on the selected nodes. */
  startSelection() {
    this._emit('request-multi-select', {
      text: 'Click nodes or draw a polygon around them to set their Break Angle attribute. Right click to delete polygon points.',
      onContinue: (nodeIds) => this._runBulkStep(nodeIds),
      onSelectAll: (nodeIds) => this._runBulkStep(nodeIds)
    });
  }

  /**
   * Resolves the given nodes' break angles, then starts the manual-review walkthrough for any that need it.
   * @param {string[]} nodeIds
   */
  async _runBulkStep(nodeIds) {
    if (!nodeIds || nodeIds.length === 0) {
      this._emit('toast', { text: 'No nodes selected.' });
      this._emit('request-cancel-prompt-action');
      return;
    }

    this._emit('progress', { phase: 'start' });

    const breakAngleAttributeService = new BreakAngleAttributeService(getFirebaseApp(), this.jobId);

    const errorMessageParts = [];
    const reviewNodeIds = [];
    for (let i = 0; i < nodeIds.length; i++) {
      const nodeId = nodeIds[i];
      try {
        const result = await breakAngleAttributeService.applyToNode(nodeId, this.connectionTypeAttribute);
        if (result.status === 'needsManualReview') reviewNodeIds.push(nodeId);
      } catch (error) {
        console.warn(`Failed to set the Break Angle attribute for node ${nodeId}`, error);
        const nodeLabel = PickAnAttribute(this.nodes[nodeId]?.attributes, this.orderingAttribute) || nodeId;
        errorMessageParts.push(`${nodeLabel} - ${error.message}`);
      }
      this._emit('progress', {
        phase: 'update',
        text: `Calculating Break Angle (${i + 1} of ${nodeIds.length})`,
        percent: ((i + 1) / nodeIds.length) * 100
      });
    }

    this._emit('progress', { phase: 'finished', text: 'Done setting Break Angle attributes.' });

    if (errorMessageParts.length != 0) {
      this._emit('warnings', { message: errorMessageParts.join('\n') });
    }

    if (reviewNodeIds.length != 0) {
      this._startReview(reviewNodeIds);
    } else {
      this._emit('request-cancel-prompt-action');
    }
  }

  /** @param {string[]} nodeIds */
  _startReview(nodeIds) {
    this._emit('request-set-active-command', { command: '_reviewBreakAngle' });
    this._reviewQueue = [...nodeIds];
    this._completedReviews = [];
    this._doNextReview(true);
  }

  _doNextReview(first) {
    if (this._reviewQueue == null) return;
    if (first !== true) {
      this._completedReviews.push(this._reviewQueue.shift());
    }

    if (this._reviewQueue.length === 0) {
      this._reviewQueue = null;
      this._completedReviews = [];
      this._emit('close-attribute-group-listener');
      this._emit('request-cancel-prompt-action');
      this._emit('toast', { text: 'Done reviewing poles with only one connection to compare.' });
      return;
    }

    const nodeId = this._reviewQueue[0];
    const node = this.nodes[nodeId];

    // The attribute's own name is too generic to use as a lookup id in the info panel (it can match unrelated
    // elements, e.g. the "add attribute" dropdown's options) - use the specific value instance's key instead, the
    // same way the app's own "add new attribute" flow does for other attributes.
    const breakAngleItemKey = Object.keys(node?.attributes?.break_angle || {})[0];

    this._emit('focus-node', { nodeId, lat: node.latitude, lng: node.longitude, zoom: 20, breakAngleItemKey });

    const isLastNode = this._reviewQueue.length === 1;
    this._emit('open-review-dialog', {
      text: 'This pole only has one connection to compare. Use the Line Angle Calculator to manually determine its Break Angle, then type it into the Break Angle field below and click Next.',
      buttons: [
        { title: 'Previous', callback: () => this._previousReview(), attributes: { grey: '' } },
        { title: isLastNode ? 'Finish' : 'Next', callback: () => this._nextReview(), attributes: { grey: '' } }
      ]
    });
  }

  _previousReview() {
    if (!this._completedReviews || this._completedReviews.length === 0) return;
    this._reviewQueue.unshift(this._completedReviews.pop());
    this._doNextReview(true);
  }

  _nextReview() {
    this._doNextReview();
  }

  /**
   * Call whenever the host's connectionTracing property changes. While the review walkthrough is running, the Line
   * Angle Calculator tool can be run mid-walkthrough to manually determine the angle. Doing so takes over the action
   * dialog and, once finished, tears the walkthrough's activeCommand/queue-cleanup down as a side effect (via the
   * host's cancelPromptAction). Pause here and resume once the Line Angle Calculator flow finishes (connectionTracing
   * flips back to false), whether it succeeded or was abandoned.
   * @param {boolean} connectionTracing
   */
  notifyConnectionTracingChanged(connectionTracing) {
    if (this._reviewQueue == null) return;
    if (connectionTracing === true) {
      this._pausedForLineAngleCalculator = true;
    } else if (this._pausedForLineAngleCalculator) {
      this._pausedForLineAngleCalculator = false;
      setTimeout(() => {
        if (this._reviewQueue == null) return;
        this._emit('request-set-active-command', { command: '_reviewBreakAngle' });
        this._doNextReview(true);
      });
    }
  }

  /**
   * Call unconditionally whenever the host cancels its current prompt/tool (e.g. from cancelPromptAction). No-ops
   * while paused for the Line Angle Calculator, since that pause is a side effect of the same cancellation mechanism
   * being reused mid-walkthrough, not a real cancel.
   */
  cancel() {
    if (this._reviewQueue != null && !this._pausedForLineAngleCalculator) {
      this._reviewQueue = null;
      this._completedReviews = [];
      this._emit('close-attribute-group-listener');
    }
  }
}

customElements.define('break-angle-review-tool', BreakAngleReviewTool);
